import React, { useState, useEffect } from "react";
import {
  DollarSign,
  Calendar,
  Save,
  X,
  Gift,
  User
} from "lucide-react";
import { toast } from "react-hot-toast";
import { addBonusApi, updateBonusApi } from "../../../app/api";
import moment from "moment";

const BonusModal = ({ isOpen, onClose, employee, onUpdate, selectedMonth }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_id: "",
    amount: "",
    month: selectedMonth || new Date().toISOString().slice(0, 7)
  });

  useEffect(() => {
    if (employee) {
      setFormData(prev => ({
        ...prev,
        user_id: employee.user_id || employee.employee_id,
        amount: employee.bonus_amount || "",
        month: selectedMonth || prev.month
      }));
    }
  }, [employee, selectedMonth]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isEdit = !!employee?.bonus_id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_id || !formData.amount) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);

      let res;
      if (isEdit) {
        // API expects: PUT /payroll/bonus/:id  body: { id, amount, month }
        const payload = {
          id: employee.bonus_id,
          amount: parseFloat(formData.amount),
          month: formData.month,
        };
        res = await updateBonusApi(employee.bonus_id, payload);
      } else {
        const payload = {
          user_id: formData.user_id,
          amount: parseFloat(formData.amount),
          month: formData.month,
        };
        res = await addBonusApi(payload);
      }

      if (res.success) {
        toast.success(isEdit ? "Bonus updated successfully!" : "Bonus added successfully!");
        onUpdate();
        onClose();
      } else {
        toast.error(res.message || `Failed to ${isEdit ? "update" : "add"} bonus`);
      }
    } catch (error) {
      console.error(`Error ${isEdit ? "updating" : "adding"} bonus:`, error);
      toast.error(`An error occurred while ${isEdit ? "saving" : "adding"} bonus`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 animate-in fade-in duration-300">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-[#1B103F] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto no-scrollbar rounded-[32px] shadow-2xl p-8 space-y-6 scrollbar-hide animate-in zoom-in-95 duration-300">

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 bg-white/5 hover:bg-white/10 text-white rounded-full transition border border-white/10"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-green-500/30">
            <Gift className="text-green-400" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight">
            {isEdit ? "Update Employee Bonus" : "Add Employee Bonus"}
          </h2>
          <p className="text-white/40 text-sm">
            {isEdit ? `Modifying bonus for ${employee?.name}` : `Reward ${employee?.name} for their hard work`}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 pt-4">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <InputGroup
                label="Employee"
                name="employee_name"
                value={employee?.name || ""}
                onChange={() => { }}
                icon={User}
                disabled={true}
              />
            </div>

            <InputGroup
              label="Bonus Amount"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleChange}
              icon={DollarSign}
              placeholder="e.g. 5000"
              required
            />

            <InputGroup
              label="Bonus Month"
              name="month"
              type="month"
              value={formData.month}
              onChange={handleChange}
              max={moment().subtract(1, 'month').format("YYYY-MM")}
              icon={Calendar}
              required
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-4 rounded-2xl bg-white/5 hover:bg-white/10 transition font-bold border border-white/10 text-white/70"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-[2] py-4 rounded-2xl bg-gradient-to-tr from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-black shadow-xl shadow-green-500/20 transition transform active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Save size={20} /> {isEdit ? "Update Bonus" : "Add Bonus"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InputGroup = ({ label, name, type = "text", value, onChange, icon: Icon, placeholder, required = false, disabled = false }) => (
  <div className="space-y-2">
    <label className="text-[10px] uppercase font-bold text-white/30 ml-1">{label}</label>
    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-green-400 transition-colors">
        <Icon size={18} />
      </div>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-3.5 text-sm focus:outline-none focus:border-green-500/50 focus:bg-white/[0.08] transition-all text-white disabled:opacity-50 disabled:cursor-not-allowed"
      />
    </div>
  </div>
);

export default BonusModal;
